/* eslint-disable react/jsx-props-no-spreading */
import React, { forwardRef, useEffect, useMemo, useState } from 'react'

import styled from '@emotion/styled'
import { Controller, useFormContext } from 'react-hook-form'
import NumberFormat, { NumberFormatValues } from 'react-number-format'

import { ArcadeInput, Input, MaskedInput as ArcadeMaskedInput } from '@cash-design-system/react'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-arcade-migration'

import { FieldController } from '../FieldController/FieldController'

type Formatter = (value: string) => string

export interface InputNumberFormatProps {
  disabled?: boolean
  readOnly?: boolean
  onBlur?: (event: React.FocusEvent) => void
  onChange: (value: string | object | undefined) => void
  value?: string
  initialFormattedValue?: string
  hasError?: boolean
  isInvalid?: boolean
  errorText?: string
  format?: string | Formatter
  transform?: (value: string) => string | object | undefined
  handleInputChange?: () => void
  isAllowed?: (values: NumberFormatValues) => boolean
  CustomInput?: React.ComponentType<React.InputHTMLAttributes<HTMLInputElement> & { inputMode?: string }>
}

export interface MaskedInputProps {
  disabled?: boolean
  readOnly?: boolean
  initialFormattedValue?: string
  endAdornment?: React.ReactNode
  name: string
  placeholder: string
  rules?: React.ComponentProps<typeof Controller>['rules'] // https://react-hook-form.com/api/useform/register#options
  format?: string | Formatter
  autoComplete?: string
  transform?: (value: string) => string | object | undefined
  onFocus?: () => void
  onBlur?: (event: React.FocusEvent) => void
  handleInputChange?: () => void
  isAllowed?: (values: NumberFormatValues) => boolean
  CustomInput?: React.ComponentType<React.InputHTMLAttributes<HTMLInputElement> & { inputMode?: string }>
}

const EndAdornmentSlot = styled.span`
  position: absolute;
  right: 1rem;
  top: 3.25rem;
  pointer-events: none;
`

const ERROR_CLASS = 'error'
const StyledInput = styled(Input)`
  /* use CSS sibiling selector to color EndAdornmentIcon to match input state */
  & + .masked-input-end-adornment {
    color: ${props => props.theme.colors.secondaryIcon};
  }

  &:focus + .masked-input-end-adornment {
    color: ${props => props.theme.colors.primary};
  }
  &.${ERROR_CLASS} + .masked-input-end-adornment {
    color: ${props => props.theme.colors.error};
  }
`

export const InputNumberFormat = forwardRef<HTMLInputElement, InputNumberFormatProps>(
  function InputNumberFormatComponent(
    {
      onChange,
      initialFormattedValue,
      value,
      hasError,
      isInvalid,
      errorText,
      readOnly,
      format,
      transform = s => s,
      handleInputChange,
      CustomInput,
      ...props
    },
    ref
  ) {
    const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')
    const [formattedValue, setFormattedValue] = useState(initialFormattedValue)

    const styledInput = useMemo(
      () => (CustomInput ? CustomInput : isArcadeMigrationEnabled ? ArcadeInput : StyledInput),
      [CustomInput, isArcadeMigrationEnabled]
    )

    useEffect(() => {
      initialFormattedValue && onChange(initialFormattedValue)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const showError = hasError || isInvalid

    return (
      <NumberFormat
        customInput={styledInput}
        format={format}
        value={formattedValue}
        className={showError ? 'error' : ''}
        readOnly={readOnly}
        onValueChange={target => {
          handleInputChange?.()
          // The formatted value is displayed in the input, while the non-formatted value
          // is passed to the parent context onChange (likely coming from react-hook form in most
          // of our usecases)
          setFormattedValue(target.formattedValue)
          onChange(transform(target.value))
        }}
        getInputRef={ref}
        {...props}
      />
    )
  }
)

export const MaskedInput = forwardRef<HTMLInputElement, MaskedInputProps>(
  ({ name, rules, disabled, readOnly, endAdornment, CustomInput, ...props }, ref) => {
    // MaskedInput must be wrapped in FormProvider from react-hook-form to access form context
    const { control } = useFormContext()
    const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')

    if (isArcadeMigrationEnabled) {
      if (props.format && CustomInput) {
        return (
          <FieldController
            FieldComponent={InputNumberFormat}
            componentProps={{
              CustomInput,
              ...props,
              disabled,
              readOnly,
            }}
            defaultValue={props.initialFormattedValue}
            name={name}
            rules={rules}
            ref={ref}
          />
        )
      }

      return (
        <FieldController
          FieldComponent={ArcadeMaskedInput}
          componentProps={{
            ...props,
            disabled,
            readOnly,
            fieldLabelProps: { label: '' },
          }}
          defaultValue={props.initialFormattedValue}
          name={name}
          rules={rules}
          ref={ref}
        />
      )
    }

    return (
      <Controller
        name={name}
        rules={rules}
        control={control}
        render={({ field, fieldState: { error, isDirty }, formState: { isSubmitted, isSubmitting } }) => {
          const { onBlur } = field
          const showError = !!error && (error?.type !== 'required' || isSubmitted)

          return (
            <>
              <InputNumberFormat
                CustomInput={CustomInput}
                {...field}
                hasError={showError}
                {...props}
                disabled={disabled || isSubmitting}
                readOnly={readOnly}
                onBlur={e => {
                  props.onBlur?.(e)
                  if (isDirty) {
                    // validate onBlur only when field is dirty
                    onBlur()
                  }
                }}
                ref={ref}
              />
              {endAdornment && (
                <EndAdornmentSlot className="masked-input-end-adornment">{endAdornment}</EndAdornmentSlot>
              )}
            </>
          )
        }}
      />
    )
  }
)
